import { useCallback, useEffect, useRef, useState } from "react";

import {
  isExtensionSetupEnd,
  useBrowserExtensionSetup,
} from "./browser-extension-setup";
import {
  listBrowserExtensions,
  type BrowserExtensionStates,
} from "./denker-browser";

type Read = () => Promise<BrowserExtensionStates | null>;

const NOT_READ: Read = () => Promise.resolve(null);

/**
 * The Denker extension's state in each browser - null until first read -
 * read when `enabled` turns on, whenever Denker's window takes focus - the
 * user comes back from adding the extension in a browser - when an
 * extension setup ends, and on `refresh` after Connect or Disconnect. Not on
 * a timer: each read asks every connected extension for its windows. A
 * failed read keeps the last one.
 */
export function useBrowserExtensions(enabled: boolean): {
  extensions: BrowserExtensionStates | null;
  refresh: Read;
} {
  const [states, setStates] = useState<BrowserExtensionStates | null>(null);
  const reader = useRef<Read>(NOT_READ);
  const setupEnded = useBrowserExtensionSetup(({ progress }) =>
    progress && isExtensionSetupEnd(progress.step) ? progress.setupId : null,
  );
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    let latest = 0;
    let reading = 0;
    const read: Read = () => {
      const mine = ++latest;
      reading += 1;
      return listBrowserExtensions()
        .then(
          (next) => {
            if (active && mine === latest) setStates(next);
            return next;
          },
          () => null,
        )
        .finally(() => {
          reading -= 1;
        });
    };
    reader.current = read;
    void read();
    const onFocus = () => {
      if (reading === 0) void read();
    };
    window.addEventListener("focus", onFocus);
    return () => {
      active = false;
      reader.current = NOT_READ;
      window.removeEventListener("focus", onFocus);
    };
  }, [enabled, setupEnded]);
  const refresh = useCallback(() => reader.current(), []);
  return { extensions: states, refresh };
}
